import { useAuthContext } from 'contexts/AuthContext';
import SendGridPoster from 'data/SendGridPoster';
import {
  EMAIL_TEMPLATES,
  greenStatesForCalendly,
  schedulerProgramLinksZoom,
  schedulerStateLinksZoom,
  stateAbbreviation,
  TEXT_MESSAGE_TEMPLATES,
  TEXT_MESSAGE_TEMPLATES_PINPOINT,
  zoomSCLiveEventsGroupConsultLinks,
} from 'fhir-sdoh/common/constants';
import { useEffect, useMemo, useState } from 'react';
import { Button, Confirm, Dropdown, Icon, Loader, Modal, Segment } from 'semantic-ui-react';
import type { FlatPatient } from 'types';
import * as FHIRUtils from 'utils/FHIRUtils';
import * as Helpers from 'utils/Helpers';
import type { PractitionerEntry } from 'utils/bundleNarrowing';

import { getCommunicationChannelPreference } from './zoomUtil';

type ZoomTargetType = 'practitioner' | 'state';

/** Orgs that book FFS MNT consults through the shared NC Zoom scheduler (initial + follow-up). */
const FFS_MNT_NC_ORGS = ['BCNC-EMPL-DM', 'BCNC-IAFP-DM', 'FFS-MNT'];

type PractitionerOption = {
  key: string;
  text: string;
  value: string;
  image: { avatar: boolean; src: string };
};

/**
 * Member-facing Zoom scheduling controls.
 *
 * Renders two cards:
 *  - **Care Team Member - Zoom**: pick a practitioner and send/schedule a Zoom invite tied to that RD's scheduler page.
 *  - **State/Organization - Zoom**: send/schedule a Zoom invite using the org/state-routed scheduler link.
 *
 * It also surfaces the temporary Zoom Group Consult / FFS Consult shortcuts for orgs that haven't been migrated to the
 * Zoom API yet.
 */
/**
 * What the Zoom scheduling panel accepts. Only call site that supplies props is
 * `components/patient/index.tsx`. The `<Zoom flatPatient={patient} />` render in
 * `components/patient/communication/index.tsx` is a DIFFERENT component of the
 * same name, not this one.
 *
 * `practitionerData` is `PractitionerEntry[]` from `utils/bundleNarrowing`.
 * `profileData`'s type comes from `getCommunicationChannelPreference`, its only
 * consumer.
 *
 * `org` is `string`, not `| undefined`: it is passed as `flatpatient.org`
 * (required on {@link FlatPatient}) and used unguarded throughout this file.
 */
type ZoomProps = {
  patient?: FlatPatient | undefined;
  patientId?: string | undefined;
  practitionerData?: PractitionerEntry[] | undefined;
  /** Gates every card on the panel while the caller's datasets are in flight. */
  loading?: boolean | undefined;
  org: string;
  profileData?: Parameters<typeof getCommunicationChannelPreference>[0] | undefined;
};

function Zoom(props: ZoomProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [notificationType, setNotificationType] = useState('');
  const [notificationMsg, setNotificationMsg] = useState('');
  const [notificationHeader, setNotificationHeader] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [zoomUrl, setZoomUrl] = useState('https://scheduler.zoom.us/');
  const [sendGridTemplateId, setSendGridTemplateId] = useState('');
  const [zoomTarget, setZoomTarget] = useState<ZoomTargetType>('practitioner');
  const [patientState, setPatientState] = useState('');

  /*
   * `mobilePhone`, `homePhone`, `state` and `email` are `unknown[]` on
   * {@link FlatPatient} — their producer is `fhirpath.evaluate`. Every consumer
   * in this file treats the first element as a `string` (`.split`, `.length`,
   * index key). The `as string` reads below record that assumption at the point
   * it is made.
   */
  const initialPhoneNumber = useMemo(() => {
    const mobile = props.patient?.mobilePhone?.[0] as string | undefined;
    const home = props.patient?.homePhone?.[0] as string | undefined;
    if (mobile && mobile !== '') return '1' + mobile.split('-').join('');
    if (home) return '1' + home.split('-').join('');
    return '';
  }, [props.patient]);

  const [zoomFormProps, setZoomFormProps] = useState({
    email: props.patient?.email?.[0] ?? '',
    location: initialPhoneNumber,
    name: '',
  });

  useEffect(() => {
    const sendGridTemplateJson = EMAIL_TEMPLATES.CALENDLY;
    if (props.org === 'IU') {
      setSendGridTemplateId(sendGridTemplateJson.IU);
    } else if (props.org === 'HA') {
      setSendGridTemplateId(sendGridTemplateJson.HA);
    } else {
      setSendGridTemplateId(sendGridTemplateJson.GENERIC);
    }

    const rawState = props.patient?.state?.[0] as string | undefined;
    const fullName = rawState?.length === 2 ? getStateByValue(stateAbbreviation, rawState) : rawState;
    setPatientState(fullName ?? '');

    setZoomFormProps({
      email: props.patient?.email?.[0] ?? '',
      location: initialPhoneNumber,
      name: '',
    });
     
  }, [props.patient, props.org]);

  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([
    {
      key: '',
      text: '',
      value: '',
      image: { avatar: true, src: '/images/' + Helpers.getImage(true) },
    },
  ]);

  const [selectedPractitionerName, setSelectedPractitionerName] = useState('');
  const [selectedPractitionerSlug, setSelectedPractitionerSlug] = useState('');

  const authInfo = useAuthContext();

  useEffect(() => {
    if (!props.practitionerData) return;
    const practitionersList: PractitionerOption[] = [];
    props.practitionerData.forEach((item: PractitionerEntry) => {
      const practitioner = FHIRUtils.parseFHIRPractitioner(item.resource);
      const isMale = practitioner.gender?.toLowerCase() === 'male';
      if (practitioner.email && practitioner.email !== '') {
        // eslint-disable-next-line no-useless-escape
        const specialChars = /[`~!#$%^&*()|+\-=?;:'"<>\{\}\[\]\\\/]/gi;
        // Same fhirpath-derived `unknown` as the patient fields above; the
        // truthiness check on the line before is what the code relies on.
        const slug = (practitioner.email as string)
          .replace(specialChars, '')
          .replace('@', '_')
          .replace('.com', '')
          .replace('wellbetty', 'nourishedrx');
        practitionersList.push({
          key: practitioner.id,
          text: practitioner.fullName,
          value: slug,
          image: { avatar: true, src: '/images/' + Helpers.getImage(isMale, practitioner?.fullName) },
        });
      }
    });
    setPractitioners(practitionersList);
  }, [props.practitionerData]);

  function handlePractitionerChange(_e: unknown, selected: { value: string }): void {
    const active = practitioners.find((p) => p.value === selected.value);
    if (!active) return;
    setSelectedPractitionerName(active.text);
    setSelectedPractitionerSlug(selected.value);

    // `split` always yields at least one element, and the last element of a
    // non-empty split is non-empty, so both index reads hit. The assertions say
    // that rather than adding fallbacks the code never had.
    const parts = active.text.trim().split(/\s+/);
    const slug = parts[0]!.toLowerCase() + '_' + parts[parts.length - 1]![0]!.toLowerCase();
    const overrideSlug: Record<string, string> = { mika_d: 'mika-d' };
    setZoomUrl('https://scheduler.zoom.us/' + (overrideSlug[slug] || slug));
  }

  /** Org/state-routed Zoom scheduler link, falling back to the program-wide health coach link. */
  const stateOrgZoomUrl = useMemo(() => {
    const stateRaw = props.patient?.state?.[0] as string | undefined;
    const abbr =
      stateRaw?.length === 2
        ? stateRaw
        : (stateAbbreviation?.[stateRaw!] ?? (patientState && stateAbbreviation?.[patientState]));
    if (['IUH-BC', 'IUH-CH'].includes(props.org)) {
      return schedulerProgramLinksZoom.iuhProgram;
    }
    if (['EW-PAAS', 'EW-HRSN-C3'].includes(props.org)) {
      return schedulerProgramLinksZoom.eatwellProgram;
    }
    if (['AH-MCAID-HOUSE'].includes(props.org)) {
      return schedulerProgramLinksZoom.allianceProgram;
    }
    if (props.org === 'BCMN-FEP') {
      return schedulerProgramLinksZoom.bcmnFepProgram;
    }
    if (props.org === 'DFH-MCAID') {
      return schedulerProgramLinksZoom.dfhMcaidProgram;
    }
    if (abbr && schedulerStateLinksZoom[abbr]) {
      return schedulerStateLinksZoom[abbr].en;
    }
    if (abbr && greenStatesForCalendly?.includes(abbr)) {
      return schedulerProgramLinksZoom.greenState;
    }
    return schedulerProgramLinksZoom.healthCoach;
  }, [props.patient?.state, props.org, patientState]);

  function sendEmail(): void {
    setConfirmOpen(false);
    const selectedUrl = zoomTarget === 'practitioner' ? zoomUrl : stateOrgZoomUrl;

    const channelPreference = getCommunicationChannelPreference(props.profileData);

    if (channelPreference === 'none') {
      setShowNotification(true);
      setNotificationType('FAIL');
      setNotificationHeader('Cannot send');
      setNotificationMsg(
        "This member's communication preference does not allow automated email or text (Do Not Contact, Phone Only, or Phone)."
      );
      return;
    }

    const payload: Record<string, unknown> = {
      subject: 'Nutrition Counseling',
      patientId: props.patientId,
      to: zoomFormProps.email,
      sender: 'Zoom',
      from: 'food@nourishedRx.com',
      templateId: sendGridTemplateId,
      dynamicTemplateData: {
        rd: zoomTarget === 'practitioner' ? selectedPractitionerName : patientState,
        calendlyURL: selectedUrl,
      },
    };

    if (zoomFormProps.location && zoomFormProps.location !== '') {
      payload['phoneNumber'] = zoomFormProps.location;
      payload['message'] = TEXT_MESSAGE_TEMPLATES['Nutrition Counseling'].replace('<CalendlyLink>', selectedUrl);
      payload['templateName'] = TEXT_MESSAGE_TEMPLATES_PINPOINT['ALL - CALENDLY'];
      payload['customVariables'] = { 'Calendly.Link': [selectedUrl] };
    }

    setLoader(true);
    const recipientLabel = zoomTarget === 'practitioner' ? selectedPractitionerName : props.org || patientState;

    const openResult = (type: 'SUCCESS' | 'FAIL', header: string, msg: string) => {
      setLoader(false);
      setShowNotification(true);
      setNotificationType(type);
      setNotificationHeader(header);
      setNotificationMsg(msg);
    };

    if (channelPreference === 'both') {
      void Promise.allSettled([
        SendGridPoster(`/hooks/mail`, authInfo.token, payload),
        SendGridPoster(`/hooks/text`, authInfo.token, payload),
      ]).then((results) => {
        const mailOk = results[0].status === 'fulfilled';
        const textOk = results[1].status === 'fulfilled';
        if (mailOk && textOk) {
          openResult(
            'SUCCESS',
            'Success',
            `${recipientLabel} zoom link has been emailed and texted to the member successfully!`
          );
        } else if (mailOk && !textOk) {
          openResult(
            'FAIL',
            'Partial success',
            `The ${recipientLabel} zoom link was emailed successfully, but sending the text failed. Please check the logs.`
          );
        } else if (!mailOk && textOk) {
          openResult(
            'FAIL',
            'Partial success',
            `The ${recipientLabel} zoom link was texted successfully, but sending the email failed. Please check the logs.`
          );
        } else {
          openResult('FAIL', 'Error', 'Sending the zoom link by email and text failed. Please check the logs.');
        }
      });
    } else if (channelPreference === 'email_only') {
      void SendGridPoster(`/hooks/mail`, authInfo.token, payload).then(
        () => {
          openResult(
            'SUCCESS',
            'Success',
            `${recipientLabel} zoom link has been emailed to the member successfully (email only).`
          );
        },
        () => {
          openResult('FAIL', 'Error', 'Oops! Email sending failed. Please check the logs.');
        }
      );
    } else {
      void SendGridPoster(`/hooks/text`, authInfo.token, payload).then(
        () => {
          openResult(
            'SUCCESS',
            'Success',
            `${recipientLabel} zoom link has been texted to the member successfully (text only).`
          );
        },
        () => {
          openResult('FAIL', 'Error', 'Oops! Text sending failed. Please check the logs.');
        }
      );
    }
  }

  const handleSendingZoom = (target: ZoomTargetType) => {
    setZoomTarget(target);
    setConfirmOpen(true);
  };

  const handleClickZoomGroup = (org: string, date?: string) => {
    if (org === 'BCSC-MCARE-PD' && date) {
      // Keyed by a date string chosen from the rendered buttons; the map holds
      // only the three live-event dates, so an unlisted date opens `undefined`.
      return window.open(zoomSCLiveEventsGroupConsultLinks[date as keyof typeof zoomSCLiveEventsGroupConsultLinks], '_blank');
    }
    if (['BCMN-MSHO-CHRO'].includes(org)) {
      return window.open('https://scheduler.zoom.us/booking-page-for-ffs-mnt-mn', '_blank');
    }
    if (org === 'DFH-MCAID') {
      return window.open(schedulerProgramLinksZoom.dfhMcaidProgram, '_blank');
    }
    if (['BCSC-MCARE-PD'].includes(org)) {
      return window.open('https://scheduler.zoom.us/booking-page-for-south-carolina-residents', '_blank');
    }
  };

  const handleClickFfsInitial = (org: string) => {
    if (FFS_MNT_NC_ORGS.includes(org)) {
      return window.open(
        'https://scheduler.zoom.us/momentum_60min_nc/initial-nutrition-assessment-60-min-zoom',
        '_blank'
      );
    }
  };

  const handleClickFfsFollowUp = (org: string) => {
    if (FFS_MNT_NC_ORGS.includes(org)) {
      return window.open('https://scheduler.zoom.us/momentum_60min_nc/nutrition-follow-up-45-min-zoom', '_blank');
    }
  };

  const confirmRecipient = zoomTarget === 'practitioner' ? selectedPractitionerName : props.org || patientState;

  return (
    <>
      <Confirm
        open={confirmOpen}
        content={`Are you sure you want to share ${confirmRecipient} zoom with the member?`}
        cancelButton="Never mind"
        confirmButton="Let's do it"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => sendEmail()}
      />

      <Modal
        open={showNotification}
        size="mini"
        content={
          <>
            <Modal.Header>
              {notificationType === 'SUCCESS' && <Icon name="thumbs up outline" size="large" color="orange" />}
              {notificationType === 'FAIL' && <Icon name="warning sign" size="large" color="orange" />}
              {notificationHeader}
            </Modal.Header>
            <Modal.Content>{notificationMsg}</Modal.Content>
          </>
        }
        onActionClick={() => setShowNotification(false)}
        actions={[{ key: 'done', icon: 'check', content: 'OK', positive: true }]}
      />

      <div
        style={{
          width: '100%',
          float: 'left',
          marginTop: '15px',
          marginBottom: '15px',
        }}
      >
        {props.loading && (
          <Segment>
            <Loader active inline="centered" />
          </Segment>
        )}

        {!props.loading && (!zoomFormProps.email || zoomFormProps.email === '') && (
          <Segment>
            <h4>
              <Icon name="video" color="blue" />
              Care Team Member - Zoom
            </h4>
            <span style={{ marginTop: '5px', color: 'red' }}> Member doesn't have an email id. </span>
          </Segment>
        )}

        {!props.loading && zoomFormProps.email && (
          <Segment style={{ height: '130px' }}>
            <h4>
              <Icon name="video" color="blue" />
              Care Team Member - Zoom
            </h4>
            <div>
              <Dropdown
                search
                scrolling
                style={{ width: '100%' }}
                placeholder="Select Care Team Member"
                inline
                options={practitioners}
                value={selectedPractitionerSlug}
                onChange={(e, value) => handlePractitionerChange(e, value as { value: string })}
              />
            </div>
            <div style={{ marginTop: '15px' }}>
              {loader && <Loader active inline="centered" />}
              {!loader && (
                <Button
                  disabled={selectedPractitionerSlug === ''}
                  style={{ width: '48%', backgroundColor: '#2185d0', color: 'white' }}
                  compact
                  onClick={() => handleSendingZoom('practitioner')}
                  content="Send Zoom"
                />
              )}
              {!loader && (
                <Button
                  disabled={selectedPractitionerSlug === ''}
                  style={{ width: '48%', backgroundColor: '#2185d0', color: 'white' }}
                  compact
                  onClick={() => window.open(zoomUrl, '_blank')}
                  content="Schedule Zoom"
                />
              )}
            </div>
          </Segment>
        )}

        {!props.loading && stateOrgZoomUrl && (
          <Segment style={{ height: '130px' }}>
            <h4>
              <Icon name="video" color="blue" />
              State/Organization - Zoom
            </h4>
            <div style={{ marginTop: '15px' }}>
              {loader && <Loader active inline="centered" />}
              {!loader && (
                <Button
                  style={{ width: '48%', backgroundColor: '#2185d0', color: 'white' }}
                  compact
                  onClick={() => handleSendingZoom('state')}
                  content="Send State/Org Zoom"
                />
              )}
              {!loader && (
                <Button
                  style={{ width: '48%', backgroundColor: '#2185d0', color: 'white' }}
                  compact
                  onClick={() => window.open(stateOrgZoomUrl, '_blank')}
                  content="Schedule State/Org Zoom"
                />
              )}
            </div>
          </Segment>
        )}

        {/* Temporary Zoom group consult section for BCSC-MCARE-PD live events; remove once Zoom API integration ships. */}
        {!props.loading && props.org === 'BCSC-MCARE-PD' && (
          <Segment style={{ height: '130px' }}>
            <h4>
              <Icon name="video" color="blue" />
              Zoom Group Consult
            </h4>
            <div>
              {Object.keys(zoomSCLiveEventsGroupConsultLinks).map((date) => (
                <span key={date}>
                  {loader && <Loader active inline="centered" />}
                  {!loader && (
                    <Button size="small" positive onClick={() => handleClickZoomGroup(props.org, date)}>
                      {date}
                    </Button>
                  )}
                </span>
              ))}
            </div>
          </Segment>
        )}

        {/* Temporary Zoom FFS consult shortcut for BCNC-EMPL-DM / BCNC-IAFP-DM; remove once Zoom API integration ships. */}
        {!props.loading && FFS_MNT_NC_ORGS.includes(props.org) && (
          <Segment style={{ height: '140px' }}>
            <h4>
              <Icon name="video" color="blue" />
              FFS Consult
            </h4>
            <div>
              {loader && <Loader active inline="centered" />}
              {!loader && (
                <>
                  <Button size="small" positive onClick={() => handleClickFfsInitial(props.org)}>
                    Schedule Initial
                  </Button>
                  <Button size="small" positive onClick={() => handleClickFfsFollowUp(props.org)}>
                    Schedule Follow-up
                  </Button>
                </>
              )}
            </div>
          </Segment>
        )}

        {/* Temporary Zoom FFS consult shortcut for BCMN-MSHO-CHRO / DFH-MCAID; remove once Zoom API integration ships. */}
        {!props.loading && ['BCMN-MSHO-CHRO', 'DFH-MCAID'].includes(props.org) && (
          <Segment style={{ height: '130px' }}>
            <h4>
              <Icon name="video" color="blue" />
              FFS Consult
            </h4>
            <div>
              {loader && <Loader active inline="centered" />}
              {!loader && (
                <Button size="small" positive onClick={() => handleClickZoomGroup(props.org)}>
                  Schedule Zoom
                </Button>
              )}
            </div>
          </Segment>
        )}
      </div>
    </>
  );
}

export default Zoom;

/**
 * Reverse lookup: the key whose value matches, or `undefined`.
 *
 * The value type admits `undefined` because the only caller passes
 * `stateAbbreviation`, which carries the app's open-key intersection
 * (`… & Record<string, string | undefined>`) and so is not a plain
 * `Record<string, string>`.
 */
function getStateByValue(object: Record<string, string | undefined>, value: string) {
  return Object.keys(object).find((key) => object[key] === value);
}
